import {
  useGetAllAsset,
  type GetAllAssetQueryResult,
  ApiAssetType,
  ApiMarketType,
  RampingAssetData,
  RampAssetType,
  useGetAdminAssets,
} from '@ping/api';
import { isListedMarket } from '@ping/helpers/access-scope.helper';
import { isMiax } from '@ping/utils';
import { IMarketDataType } from '@ping/websockets/useMarketMoneyWS';
import { parsePair } from './parse-pair.helper';
import { useMemo } from 'react';
import { isCommodities, isStock, isForex } from '@ping/utils/checkCompany';

/**
 * Higher-order function to filter assets for MIAX mode
 * When in MIAX mode, only shows commodity assets
 */
export function withMiaxFilter(hookResult: ReturnType<typeof useGetAllAsset>) {
  const result = hookResult as { data: GetAllAssetQueryResult } & ReturnType<typeof useGetAllAsset>;

  const filtered = useMemo(() => {
    if (!isCommodities()) {
      return result.data?.data.filter(isAccessibleAsset);
    }

    return result.data?.data.filter(isMiaxAccessibleAsset);
  }, [result.data]);

  return { ...result, data: filtered };
}

export function withCommoditiesAdminFilter(hookResult: ReturnType<typeof useGetAdminAssets>) {
  const result = hookResult as GetAllAssetQueryResult & ReturnType<typeof useGetAdminAssets>;

  const filtered = useMemo(() => {
    if (!isCommodities()) {
      return result.data?.filter(isAccessibleAsset);
    }

    return result.data?.filter(isMiaxAccessibleAsset);
  }, [result.data]);

  return { ...result, data: filtered };
}

/**
 * Hook to get commodity asset symbols for MIAX filtering
 * @returns Object with commodity asset symbols array
 */
export function useMiaxCommodityAssets() {
  const { data: assetsData, isLoading } = withMiaxFilter(useGetAllAsset());

  const commoditySymbols = useMemo(() => {
    if (!assetsData) {
      return [];
    }

    // withMiaxFilter already filters for commodities in MIAX mode
    return assetsData.map((asset: any) => asset.symbol || asset.id).filter(Boolean);
  }, [assetsData]);

  return { data: commoditySymbols, isLoading };
}

/**
 * Filter function for ramp assets in MIAX mode
 * @param assets Array of ramp assets to filter
 * @param commoditySymbols Array of commodity asset symbols
 * @returns Filtered assets array
 */
export function filterMiaxRampAssets(assets: RampingAssetData[], commoditySymbols: string[]): RampingAssetData[] {
  if (!isCommodities) {
    return assets;
  }

  return assets.filter(asset => {
    // Allow USDC and USDX always
    if (['usdc', 'usdx'].includes(asset.symbol.toLowerCase())) {
      return true;
    }
    // For crypto assets, check if they're in the commodity list
    if (asset.type === RampAssetType.Crypto) {
      return commoditySymbols.includes(asset.symbol);
    }
    // Allow fiat assets
    return asset.type === RampAssetType.Fiat;
  });
}

/**
 * Sort function to prioritize USD-based fiat currencies in MIAX mode
 * @param assets Array of ramp assets to sort
 * @returns Sorted assets array
 */
export function sortMiaxFiatAssets(assets: RampingAssetData[]): RampingAssetData[] {
  if (!isMiax()) {
    return assets;
  }

  return assets.sort((a, b) => {
    // Prioritize USD-based currencies
    const aIsUSD = a.symbol.includes('USD');
    const bIsUSD = b.symbol.includes('USD');

    if (aIsUSD && !bIsUSD) return -1;
    if (!aIsUSD && bIsUSD) return 1;

    return a.symbol.localeCompare(b.symbol);
  });
}

/**
 * Filter individual market data items for MIAX mode
 * @param data Market data item
 * @param commodityAssetSymbols Array of commodity asset symbols
 * @returns true if the item should be included
 */
export function filterMiaxMarketData(data: IMarketDataType, commodityAssetSymbols: string[]): boolean {
  if (!isCommodities()) {
    return true;
  }

  // Exclude Invisible markets (Internal is listed for tagged users; FO sets hidden=true for Internal).
  if (!isListedMarket(data as { visibility?: string; hidden?: boolean })) {
    return false;
  }

  const { base, quote } = parsePair.byInstrument(data.instrument || '');

  // Allow USDX_USDC market
  if (base.toLowerCase() === 'usdx' && quote.toLowerCase() === 'usdc') {
    return true;
  }

  // Allow if base asset is commodity and quote is USDC or USDX
  return (
    commodityAssetSymbols.includes(base.toLowerCase()) &&
    (quote.toLowerCase() === 'usdc' || quote.toLowerCase() === 'usdx')
  );
}

/**
 * Filter market pairs based on selected exchange type
 * @param marketData Market data item
 * @param marketInfo Market info with marketType from useGetInfo
 * @returns true if the market pair should be included
 */
export function filterMiaxMarketPairs(marketData: IMarketDataType, marketInfo?: any): boolean {
  const instrument = marketData.instrument || '';
  const instrumentInfo = marketInfo?.[instrument];

  // Exclude Invisible markets (Internal is listed for tagged users; FO sets hidden=true for Internal).
  if (instrumentInfo && !isListedMarket(instrumentInfo)) {
    return false;
  }

  // If marketInfo is not available or marketType is not set, show all non-hidden markets
  if (!instrumentInfo || !instrumentInfo.marketType) {
    return true;
  }

  // Filter based on selected exchange type
  if (isStock()) {
    return instrumentInfo.marketType === ApiMarketType.Stock;
  }

  if (isForex()) {
    return instrumentInfo.marketType === ApiMarketType.Forex;
  }

  if (isCommodities()) {
    return instrumentInfo.marketType === ApiMarketType.Commodity;
  }

  // Default: crypto mode - only show cryptocurrency markets
  return instrumentInfo.marketType === ApiMarketType.Cryptocurrency;
}

/** Catalog entries are accessible when at least one returned network config is accessible. */
export function isAccessibleAsset(asset) {
  const configs = asset?.blockchain_configs ?? asset?.blockchainConfigs ?? [];
  return configs.some(config => isAccessibleBlockchainConfig(config));
}

export function isAccessibleAssetNetwork(asset, blockchainNetwork: string | null | undefined) {
  if (!blockchainNetwork) return isAccessibleAsset(asset);
  const configs = asset?.blockchain_configs ?? asset?.blockchainConfigs ?? [];
  const config = configs.find(
    item =>
      String(item?.blockchain_network ?? item?.blockchainNetwork).toLowerCase() === blockchainNetwork.toLowerCase()
  );
  return isAccessibleBlockchainConfig(config);
}

function isAccessibleBlockchainConfig(config) {
  const visibility = String(config?.visibility ?? '').toLowerCase();
  return (
    config != null &&
    (visibility === '' ||
      visibility === 'visible' ||
      visibility === '1' ||
      visibility === 'internal' ||
      visibility === '2')
  );
}

export function isMiaxAsset(asset) {
  // For Stock mode, only show Stock assets and base currencies
  if (isStock()) {
    return ['usdc', 'usdx'].includes(asset.id.toLowerCase()) || asset.type === ApiAssetType.Stock;
  }

  // For Forex mode, only show Forex assets and base currencies
  if (isForex()) {
    return ['usdc', 'usdx'].includes(asset.id.toLowerCase()) || asset.type === ApiAssetType.Forex;
  }

  // For Commodities mode, only show Commodity assets and base currencies
  if (isCommodities()) {
    return ['usdc', 'usdx'].includes(asset.id.toLowerCase()) || asset.type === ApiAssetType.Commodity;
  }

  // For Crypto mode, exclude Commodity, Stock, and Forex assets
  return (
    asset.type !== ApiAssetType.Commodity && asset.type !== ApiAssetType.Stock && asset.type !== ApiAssetType.Forex
  );
}

export function isMiaxAccessibleAsset(asset) {
  return isAccessibleAsset(asset) && isMiaxAsset(asset);
}
